import { node } from "~/util/dom";
import { CLOSE_24 } from "@sznds/icons";
import createSDSIcon from "~/ui/icon";

interface IDialogOptions {
	modal?: boolean;
	closeOnBackdropClick?: boolean;
	onClose?: () => void;
	onOpen?: () => void;
}

export function createDialogCloseButton() {
	const button = node("button", { type: "button", className: "ui-dialogCloseButton" });

	button.append(createSDSIcon({ symbol: CLOSE_24, role: "presentation" }));

	return button;
}

export function createDialogButton({ className = "", primary = false, ...params }, text) {
	return node("button", { type: "button", className: `ui-dialogButton ${primary ? "ui-dialogButton--primary" : ""} ${className}`, ...params }, text);
}

export default class Dialog {
	/** element dialogu */
	#container: HTMLDialogElement;

	/** nastaveni dialogu */
	#options: IDialogOptions;

	/** reference na posledni aktivni element, tedy na toho, komu vratime pri zavreni focus */
	#lastActiveElement: HTMLElement | null;

	constructor(options: IDialogOptions = {}) {
		// nastavime defaultni hodnoty
		this.#options = {
			modal: true,
			closeOnBackdropClick: true,
			...options,
		};
		this.#lastActiveElement = null;
		this.#container = node("dialog", { className: "ui-dialog" });

		// pokud ma klikani na backdrop zavirat dialog, pripojime posluchac udalosti
		if (this.#options.closeOnBackdropClick) {
			this.#container.addEventListener("click", event => {
				const rect = this.#container.getBoundingClientRect();

				if (rect.left > event.clientX || rect.right < event.clientX || rect.top > event.clientY || rect.bottom < event.clientY) {
					this.close();
				}
			});
		}
	}

	get container() {
		return this.#container;
	}

	open() {
		if (this.#container.open) {
			return;
		}

		// zapamatujeme si, kdo nas otevrel
		this.#lastActiveElement = document.activeElement as HTMLElement;

		document.body.appendChild(this.#container);

		if (this.#options.modal) {
			this.#container.showModal();
		} else {
			this.#container.show();
		}

		// pokud neexistuje potomek s autofocusem, tak focusneme prvniho potomka, ktery muze dostat focus, coz je v drtive vetsine pripadu zaviraci tlacitko
		if (!this.#container.querySelector("[autofocus]")) {
			const focusableElements = this.#container.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");

			if (focusableElements.length > 0) {
				(focusableElements[0] as HTMLElement).focus?.();
			}
		}
		this.#options.onOpen?.();
	}

	close() {
		if (!this.#container.open) {
			return;
		}

		this.#container.close();
		this.#container.remove();
		this.#options.onClose?.();

		// vratime focus
		this.#lastActiveElement?.focus?.();
	}

	destroy() {
		if (this.#container.open) {
			this.close();
		}
	}
}
